import { useState, useEffect } from "react"
import { Button, Stack, Alert, Spinner } from "react-bootstrap"
import { SettingSection, FIELD_NAMES, ValidationRequest } from "../../api/company/data"
import { useCompanySettingsStore } from "../../api/company/store"
import useUXStore from "../../api/ux/store"
import CStack from "../../components/CStack"
import SwitchControl from "../../components/SwitchControl"
import InputItem from "../../components/InputItem"
import BaseControl from "../../components/BaseControl"

interface SettingsSectionProps {
    section: string
    data: SettingSection
}

const SettingsSection = ({ section, data }: SettingsSectionProps) => {
    const { updateSection, validateSection } = useCompanySettingsStore(state => state)
    const { addToast } = useUXStore(state => state)
    
    const [formData, setFormData] = useState<{ [key: string]: any }>(data?.data || {})
    const [errors, setErrors] = useState<{ [key: string]: string }>({})
    const [saving, setSaving] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)
    
    useEffect(() => {
        setFormData(data?.data || {})
        setHasChanges(false)
    }, [data])
    
    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        setHasChanges(true)
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev }
                delete newErrors[field]
                return newErrors
            })
        }
    }
    
    const handleValidate = async () => {
        const request: ValidationRequest = {
            section: section,
            settings: formData
        }
        
        try {
            const response = await validateSection(request)
            if (!response.valid && response.errors) {
                setErrors(response.errors)
                addToast("Warning", "Validation failed. Please check the errors.")
                return false
            }
            setErrors({})
            addToast("Success", "Settings validated successfully")
            return true
        } catch (error) {
            addToast("Error", "Failed to validate settings")
            return false
        }
    }
    
    const handleSave = async () => {
        setSaving(true)
        addToast("Info", "Applying settings...")
        
        // Validate first
        const isValid = await handleValidate()
        if (!isValid) {
            setSaving(false)
            return
        }
        
        try {
            const updatedSection: SettingSection = {
                data: formData,
                encrypted_fields: data?.encrypted_fields
            }
            await updateSection(section, updatedSection)
            addToast("Success", "Settings applied successfully")
            setHasChanges(false)
        } catch (error: any) {
            addToast("Error", error.message || "Failed to apply settings")
        } finally {
            setSaving(false)
        }
    }
    
    const renderField = (field: string, value: any) => {
        const fieldName = FIELD_NAMES[section]?.[field] || field
        const isEncrypted = data?.encrypted_fields?.includes(field)
        const error = errors[field]
        
        // Add caption based on field type or error
        let caption = ""
        if (error) {
            caption = error
        } else if (isEncrypted) {
            caption = "This field is encrypted"
        }
        
        // Render different input types based on the value type
        if (typeof value === 'boolean') {
            return (
                <SwitchControl
                    key={field}
                    text={fieldName}
                    caption={caption}
                    edit={true}
                    value={formData[field] || false}
                    onChanged={(val) => handleChange(field, val)}
                />
            )
        }
        
        // For number and text inputs
        return (
            <InputItem
                key={field}
                id={field}
                text={fieldName}
                caption={caption}
                type={typeof value === 'number' ? 'number' : (isEncrypted ? 'password' : 'text')}
                value={formData[field] || (typeof value === 'number' ? 0 : '')}
                edit={true}
                inputSize={6}
                onChanged={(id, val) => handleChange(field, typeof value === 'number' ? (parseInt(val) || 0) : val)}
            />
        )
    }
    
    if (!data) {
        return <Alert variant="info">No settings available for this section</Alert>
    }
    
    return (
        <CStack padding="0">
            {Object.entries(formData).map(([field, value]) => renderField(field, value))}
            
            {hasChanges && <BaseControl text="Apply changes?">
                <Stack direction="horizontal" gap={2}>
                    <Button 
                        size="sm" 
                        onClick={() => {
                            setFormData(data?.data || {})
                            setHasChanges(false)
                            setErrors({})
                        }} 
                        variant="outline-secondary"
                    >
                        Cancel
                    </Button>
                    <Button 
                        size="sm" 
                        disabled={saving} 
                        onClick={handleSave} 
                        variant="primary"
                    >
                        {!saving ? "Apply" : <Stack direction="horizontal" gap={1}><Spinner size="sm" />Applying</Stack>}
                    </Button>
                </Stack>
            </BaseControl>}
        </CStack>
    )
}

export default SettingsSection